import type { FunctionComponent } from 'preact'
import { useRef, useEffect, useMemo, useState } from 'preact/hooks'
import type { ChatMessage, AIAction, ContextualField } from '@/types/aiChat'
import type { QuizDto, QuizDraftDto } from '@/types/api-generated'
import { parseWorkingQuiz, QUIZ_AI_FIELD, type QuizDraftDescription } from '@/utils/quizDraft'
import { WebsterIcon } from './WebsterIcon'
import { ActivityStream } from './ActivityStream'
import { QuizDraftCard } from './QuizDraftCard'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface ChatMessagesProps {
  messages: ChatMessage[]
  isLoading: boolean
  onSelectVariation: (messageId: string, variationIndex: number, value: string) => Promise<boolean>
  onSelectAllVariations: (messageId: string, variations: string[]) => Promise<boolean>
  onExecuteAction: (action: AIAction) => void
  availableFields: ContextualField[]
  /** The web the chat context is on; a quiz draft goes only into the editor of the web it was checked against. */
  contextWebId?: string
  onMessageFeedback: (messageId: string, rating: boolean, comment?: string) => void
  onPutQuizDraftInEditor: (messageId: string, draft: QuizDraftDto, description: QuizDraftDescription) => Promise<boolean>
}

export const ChatMessages: FunctionComponent<ChatMessagesProps> = ({
  messages,
  isLoading,
  onSelectVariation,
  onSelectAllVariations,
  onExecuteAction,
  availableFields,
  contextWebId,
  onMessageFeedback,
  onPutQuizDraftInEditor,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // The Quiz field changes on every keystroke in the editor, so it is parsed once per value, not per card.
  const quizField = availableFields.find(field => field.name === QUIZ_AI_FIELD)
  const quizFieldValue = quizField?.currentValue
  const workingQuiz = useMemo(() => parseWorkingQuiz(quizFieldValue), [quizFieldValue])
  const canPutQuizDraftInEditor = quizField?.isEditable === true

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Auto-scroll to bottom when new messages arrive or loading state changes
  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading])

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="w-24 h-24 mb-4 rounded-full bg-slate-900 dark:bg-slate-700 flex items-center justify-center overflow-visible">
            <WebsterIcon size={86} state="aware" />
          </div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Webster Ready</h4>
          <p className="text-xs text-gray-500 max-w-xs">
            Ask me anything about your content, or request help with the fields you're editing.
          </p>
        </div>
      )}

      {messages.map(message => (
        <MessageBubble
          key={message.id}
          message={message}
          onSelectVariation={onSelectVariation}
          onSelectAllVariations={onSelectAllVariations}
          onExecuteAction={onExecuteAction}
          availableFields={availableFields}
          onMessageFeedback={onMessageFeedback}
          onLayoutChange={scrollToBottom}
          workingQuiz={workingQuiz}
          canPutQuizDraftInEditor={canPutQuizDraftInEditor}
          contextWebId={contextWebId}
          onPutQuizDraftInEditor={onPutQuizDraftInEditor}
        />
      ))}

      {isLoading && <LoadingIndicator />}

      <div ref={messagesEndRef} />
    </div>
  )
}

// --- Sub-components extracted from MessageBubble ---

/** Strip variation list from message content, showing only the intro text */
function getIntroContent(content: string): string {
  const lines = content.split('\n')
  const introLines: string[] = []

  for (const line of lines) {
    if (/^\s*(\d+\.|[-*]|\[)/.test(line)) break
    introLines.push(line)
  }

  return introLines.join('\n').trim()
}

/** Strip markdown bold/italic and surrounding quotes from a variation string */
function cleanVariationText(variation: string): string {
  return variation
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .trim()
}

interface ThumbsDownCommentProps {
  comment: string
  onCommentChange: (value: string) => void
  onSubmit: () => void
  onSkip: () => void
}

const ThumbsDownComment: FunctionComponent<ThumbsDownCommentProps> = ({
  comment,
  onCommentChange,
  onSubmit,
  onSkip,
}) => (
  <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
    <p className="text-xs text-red-700 dark:text-red-400 mb-1.5">What went wrong?</p>
    <textarea
      value={comment}
      onInput={(e) => onCommentChange((e.target as HTMLTextAreaElement).value)}
      placeholder="Optional feedback..."
      rows={2}
      className="w-full px-2 py-1.5 text-xs border border-red-200 dark:border-red-700 rounded
               bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200
               focus:outline-none focus:ring-1 focus:ring-red-400 resize-none
               placeholder:text-gray-400 dark:placeholder:text-gray-500"
    />
    <div className="flex justify-end gap-2 mt-1.5">
      <button
        type="button"
        onClick={onSkip}
        className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
      >
        Skip
      </button>
      <button
        type="button"
        onClick={onSubmit}
        className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
      >
        Submit
      </button>
    </div>
  </div>
)

interface VariationListProps {
  messageId: string
  variations: string[]
  isListField: boolean
  onSelectVariation: (messageId: string, variationIndex: number, value: string) => Promise<boolean>
  onSelectAllVariations: (messageId: string, variations: string[]) => Promise<boolean>
}

const VariationList: FunctionComponent<VariationListProps> = ({
  messageId,
  variations,
  isListField,
  onSelectVariation,
  onSelectAllVariations,
}) => {
  const [applied, setApplied] = useState<number | 'all' | null>(null)
  const isApplied = applied !== null

  const handleUseThis = async (idx: number, variation: string) => {
    if (isApplied) return
    const success = await onSelectVariation(messageId, idx, variation)
    if (success) setApplied(idx)
  }

  const handleUseAll = async () => {
    if (isApplied) return
    const success = await onSelectAllVariations(messageId, variations.map(v => cleanVariationText(v)))
    if (success) setApplied('all')
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-2">Generated options:</p>
      {variations.map((variation, idx) => {
        const isThisApplied = applied === idx || applied === 'all'

        return (
          <div
            key={idx}
            className={`group p-3 rounded-lg border transition-all ${
              isThisApplied
                ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                : isApplied
                  ? 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 opacity-50'
                  : 'bg-white dark:bg-slate-700/50 border-gray-200 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm dark:hover:bg-slate-700 cursor-pointer'
            }`}
            onClick={() => handleUseThis(idx, variation)}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">{cleanVariationText(variation)}</span>
              {isThisApplied ? (
                <span className="text-xs text-green-600 dark:text-green-400 font-medium whitespace-nowrap flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Applied
                </span>
              ) : !isApplied ? (
                <button type="button" className="text-xs text-blue-600 dark:text-blue-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  Use This
                </button>
              ) : null}
            </div>
          </div>
        )
      })}
      {isListField && variations.length > 1 && (
        <button
          type="button"
          onClick={handleUseAll}
          disabled={isApplied}
          className={`w-full mt-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
            applied === 'all'
              ? 'border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 cursor-default'
              : isApplied
                ? 'border-gray-200 dark:border-slate-700 text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-slate-800 cursor-default opacity-50'
                : 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-400 dark:hover:border-blue-500'
          }`}
        >
          {applied === 'all' ? (
            <span className="flex items-center justify-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              All Applied
            </span>
          ) : (
            `Use All (${variations.length} items)`
          )}
        </button>
      )}
    </div>
  )
}

interface InlineThumbsProps {
  rating?: boolean | null
  showCommentInput: boolean
  onThumbsUp: () => void
  onThumbsDown: () => void
}

const InlineThumbs: FunctionComponent<InlineThumbsProps> = ({
  rating,
  showCommentInput,
  onThumbsUp,
  onThumbsDown,
}) => (
  <div className="flex items-center gap-0.5">
    <button
      type="button"
      onClick={onThumbsUp}
      disabled={rating != null || showCommentInput}
      className={`p-0.5 rounded transition-colors ${
        rating === true
          ? 'text-green-600'
          : 'text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-500'
      } disabled:cursor-default`}
      title="Thumbs up"
    >
      <svg className="w-3.5 h-3.5" fill={rating === true ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
      </svg>
    </button>
    <button
      type="button"
      onClick={onThumbsDown}
      disabled={rating != null || showCommentInput}
      className={`p-0.5 rounded transition-colors ${
        rating === false
          ? 'text-red-600'
          : 'text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-500'
      } disabled:cursor-default`}
      title="Thumbs down"
    >
      <svg className="w-3.5 h-3.5" fill={rating === false ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
      </svg>
    </button>
  </div>
)

// --- Main MessageBubble ---

interface MessageBubbleProps {
  message: ChatMessage
  onSelectVariation: (messageId: string, variationIndex: number, value: string) => Promise<boolean>
  onSelectAllVariations: (messageId: string, variations: string[]) => Promise<boolean>
  onExecuteAction: (action: AIAction) => void
  availableFields: ContextualField[]
  onMessageFeedback: (messageId: string, rating: boolean, comment?: string) => void
  onLayoutChange?: () => void
  workingQuiz: QuizDto | null
  canPutQuizDraftInEditor: boolean
  contextWebId?: string
  onPutQuizDraftInEditor: (messageId: string, draft: QuizDraftDto, description: QuizDraftDescription) => Promise<boolean>
}

function formatTime(date: Date | string): string {
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const MessageBubble: FunctionComponent<MessageBubbleProps> = ({
  message,
  onSelectVariation,
  onSelectAllVariations,
  onExecuteAction,
  availableFields,
  onMessageFeedback,
  onLayoutChange,
  workingQuiz,
  canPutQuizDraftInEditor,
  contextWebId,
  onPutQuizDraftInEditor,
}) => {
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'
  const [showCommentInput, setShowCommentInput] = useState(false)
  const [comment, setComment] = useState('')

  const handleThumbsDown = () => {
    setShowCommentInput(true)
    setTimeout(() => onLayoutChange?.(), 50)
  }

  const submitThumbsDown = () => {
    onMessageFeedback(message.id, false, comment.trim() || undefined)
    setShowCommentInput(false)
    setComment('')
  }

  const skipComment = () => {
    onMessageFeedback(message.id, false)
    setShowCommentInput(false)
    setComment('')
  }

  // A quiz draft is shown as its card in place of options; the server parses none on the Quiz tab anyway.
  const quizDraft = !isUser ? message.quizDraft : undefined
  const isQuizDraftForAnotherWeb = !!quizDraft && quizDraft.WebId !== contextWebId
  const hasVariations = !isUser && !quizDraft && message.generatedVariations && message.generatedVariations.length > 0
  const hasSuggestedActions = !isUser && message.suggestedActions && message.suggestedActions.length > 0
  const targetFieldMeta = message.targetFieldName
    ? availableFields.find(f => f.name === message.targetFieldName)
    : undefined
  const isListField = targetFieldMeta?.isListField === true

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%]">
          <div className="bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 rounded-2xl rounded-br-md px-4 py-2 text-sm whitespace-pre-wrap">
            {message.content}
          </div>
          <div className="text-xs text-slate-400 dark:text-slate-500 mt-1 text-right">
            {formatTime(message.timestamp)}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3">
      <div className="shrink-0 mt-0.5">
        <WebsterIcon size={28} state="aware" />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        {message.steps && <ActivityStream steps={message.steps} />}
        <div className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-headings:my-2 prose-code:text-xs prose-pre:my-2 text-slate-800 dark:text-slate-100">
          <Markdown remarkPlugins={[remarkGfm]}>
            {hasVariations ? getIntroContent(message.content) : message.content}
          </Markdown>
        </div>

        {/* Footer: timestamp + per-message feedback thumbs */}
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {formatTime(message.timestamp)}
          </span>
          {isAssistant && (
            <InlineThumbs
              rating={message.rating}
              showCommentInput={showCommentInput}
              onThumbsUp={() => onMessageFeedback(message.id, true)}
              onThumbsDown={handleThumbsDown}
            />
          )}
        </div>

        {/* Thumbs down comment input */}
        {showCommentInput && (
          <ThumbsDownComment
            comment={comment}
            onCommentChange={setComment}
            onSubmit={submitThumbsDown}
            onSkip={skipComment}
          />
        )}

        {/* Variations */}
        {hasVariations && (
          <VariationList
            messageId={message.id}
            variations={message.generatedVariations!}
            isListField={isListField}
            onSelectVariation={onSelectVariation}
            onSelectAllVariations={onSelectAllVariations}
          />
        )}

        {quizDraft && (
          <QuizDraftCard
            draft={quizDraft}
            workingQuiz={isQuizDraftForAnotherWeb ? null : workingQuiz}
            canPutInEditor={canPutQuizDraftInEditor && !isQuizDraftForAnotherWeb}
            isForAnotherWeb={isQuizDraftForAnotherWeb}
            appliedDescription={message.quizDraftApplied ?? null}
            onPutInEditor={description => onPutQuizDraftInEditor(message.id, quizDraft, description)}
          />
        )}

        {/* Suggested Actions */}
        {hasSuggestedActions && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.suggestedActions!.map((action, idx) => (
              <button
                type="button"
                key={idx}
                onClick={() => onExecuteAction(action)}
                className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs
                           rounded-lg transition-colors flex items-center gap-1.5"
              >
                {action.icon && <span>{action.icon}</span>}
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Error state */}
        {message.error && (
          <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
            {message.error}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Shown while a turn is in flight.
 *
 * It says only two things it can prove: that a turn is running, and how long it has been running.
 * The 22-phrase rotator this replaced asserted specific work — "Searching the knowledge base…",
 * "Cross-referencing your notes…" — on an 8-second timer with no connection to what the model was
 * doing, so most of what it showed was false. The steps that actually ran are rendered by
 * ActivityStream once the turn returns; until the loop streams, an elapsed counter is the whole of
 * what is honestly known mid-turn.
 */
const LoadingIndicator: FunctionComponent = () => {
  // Starts at 1, not 0. The indicator mounts a full second before the first tick, so counting from
  // the true elapsed value opens on "Working… 0s", which reads as stopped rather than starting.
  const [elapsedSeconds, setElapsedSeconds] = useState(1)

  useEffect(() => {
    const interval = setInterval(() => setElapsedSeconds(s => s + 1), 1000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="flex gap-3">
      <div className="shrink-0 mt-0.5">
        <WebsterIcon size={28} state="thinking" />
      </div>
      <span className="text-sm text-slate-500 dark:text-slate-400 pt-0.5 tabular-nums">
        Working… {elapsedSeconds}s
      </span>
    </div>
  )
}
